import { useTranslation } from 'react-i18next';
import { FormFieldMessage, Input } from '@/components/ui';

interface AppUriListFieldProps {
    legend: string;
    required?: boolean;
    hint?: string;
    values: string[];
    onChange: (values: string[]) => void;
    onBlur?: () => void;
    error?: string | null;
    placeholder: string;
    addLabel: string;
    /** EAA-1846 (App edit, frame 51): renders the design's "đã đổi" badge next
     *  to the legend — kept a boolean rather than widening `legend` itself to
     *  `ReactNode`, since `legend` also feeds plain-string aria-labels below. */
    changed?: boolean;
}

/**
 * EAA-1817: dynamic list editor behind "Địa chỉ chuyển về sau đăng nhập" /
 * "Địa chỉ sau khi đăng xuất" (`redirect_uris`/`post_logout_redirect_uris` —
 * both plain `string[]` on `AdminRegistryApp`). Frame 47 draws a single text
 * field, but the field it writes to is an array, so this renders one row per
 * entry with add/remove rather than asking the operator to invent a
 * delimiter for a multi-value field a bare `<input>` cannot represent.
 */
export function AppUriListField({
    legend,
    required,
    hint,
    values,
    onChange,
    onBlur,
    error,
    placeholder,
    addLabel,
    changed,
}: AppUriListFieldProps) {
    const { t } = useTranslation();

    function updateAt(index: number, value: string) {
        onChange(values.map((v, i) => (i === index ? value : v)));
    }
    function removeAt(index: number) {
        // Always keep at least one row so the field never disappears entirely.
        onChange(values.length > 1 ? values.filter((_, i) => i !== index) : ['']);
    }
    function addRow() {
        onChange([...values, '']);
    }

    return (
        <fieldset className="apps-uri-list">
            <legend>
                {legend}
                {required && <span aria-hidden="true"> *</span>}
                {changed && (
                    <span className="app-edit-field-badge app-edit-field-badge--changed">
                        {t('console.appDetail.edit.changedBadge')}
                    </span>
                )}
            </legend>
            {!error && <FormFieldMessage hint={hint} />}
            {values.map((value, index) => (
                <div className="apps-uri-list__row" key={index}>
                    {/* EAA-1817: raw <input> → kit Input (compliance sweep). */}
                    <Input
                        value={value}
                        onChange={(e) => updateAt(index, e.target.value)}
                        onBlur={onBlur}
                        placeholder={placeholder}
                        aria-label={t('console.apps.uriListField.rowAriaLabel', {
                            legend,
                            index: index + 1,
                        })}
                    />
                    <button
                        type="button"
                        className="apps-uri-list__remove"
                        onClick={() => removeAt(index)}
                        aria-label={t('console.apps.uriListField.removeAriaLabel', {
                            legend: legend.toLowerCase(),
                            index: index + 1,
                        })}
                        disabled={values.length === 1 && !value}
                    >
                        ×
                    </button>
                </div>
            ))}
            <button type="button" className="apps-uri-list__add" onClick={addRow}>
                + {addLabel}
            </button>
            <FormFieldMessage error={error} />
        </fieldset>
    );
}
